/**
 * Per-offer reset time, for the affiliate-facing dashboard.
 *
 * Each offer's cap day rolls over at `reset_hour`:00 in its own timezone (cap-db
 * `offers.reset_timezone` + `offers.reset_hour`). Affiliates are spread across many countries,
 * so a boundary stated only in the offer's zone makes every one of them do timezone arithmetic
 * before they know whether they can still run today.
 *
 * ⚠️ Three clocks, and they are NOT interchangeable:
 *
 *   - the OFFER's clock — where the advertiser's day rolls over;
 *   - TRUE UTC — the absolute instant, the one form an affiliate and an admin in different
 *     countries can quote to each other (and the form the cap manager, Everflow and support
 *     threads all use);
 *   - the VIEWER's clock — what the affiliate actually acts on.
 *
 * The tag shows the viewer's own clock (auto-detected from the browser, never picked) with the
 * true UTC instant beside it. Neither alone is sufficient: viewer time can't be quoted to
 * anyone, and UTC is not the clock on their wall.
 *
 * ⚠️ The zone is an IANA NAME, never a fixed offset. Pacific midnight is 07:00 UTC under DST and
 * 08:00 UTC otherwise, so an offset would drift the boundary an hour twice a year.
 */

const DEFAULT_ZONE = 'UTC'

function safeZone(z: string | null | undefined): string {
  const tz = (z || DEFAULT_ZONE).trim() || DEFAULT_ZONE
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return tz
  } catch {
    return DEFAULT_ZONE
  }
}

/** The offer-local reset hour, clamped to 0-23 — mirrors cap-api's `resolveResetHour`. */
export function resolveResetHour(raw: number | string | null | undefined): number {
  const h = Math.trunc(Number(raw ?? 0))
  return Number.isFinite(h) && h >= 0 && h <= 23 ? h : 0
}

/** Wall-clock parts of `at` as seen in `tz`. */
function zonedParts(tz: string, at: Date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(at)
  const p: Record<string, string> = {}
  for (const part of parts) if (part.type !== 'literal') p[part.type] = part.value
  return {
    year: Number(p.year), month: Number(p.month), day: Number(p.day),
    // `% 24` guards the ICU builds that still render midnight as hour 24.
    hour: Number(p.hour) % 24, minute: Number(p.minute), second: Number(p.second),
  }
}

/** Offset of `tz` from UTC at instant `at`, in ms (positive = ahead of UTC). */
function offsetMsAt(tz: string, at: Date): number {
  const p = zonedParts(tz, at)
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
    - Math.floor(at.getTime() / 1000) * 1000
}

/**
 * The next reset instant for an offer, as a true-UTC Date.
 *
 * Two passes over the zone offset — read at the naive instant, then re-read at the corrected one
 * — which is what makes a DST-transition day land on the real local boundary. Mirrors cap-api
 * `utils/timezone.ts`; a single-pass implementation is wrong on one transition day a year.
 *
 * This is the CLIENT-SIDE derivation, used to label every offer in the switcher. For the offer
 * currently on screen, prefer the server's own answer (`next_reset_utc`, or
 * `overflow_window.resets_at_utc`) — same instant, but computed where the cap day is decided.
 */
export function nextResetAt(
  tz: string | null | undefined,
  hour: number | null | undefined,
  now: Date = new Date(),
): Date {
  const z = safeZone(tz)
  const h = resolveResetHour(hour)
  const p = zonedParts(z, now)
  // Before today's boundary, the next reset is today's; at or after it, tomorrow's.
  const dayOffset = p.hour >= h ? 1 : 0
  const naive = Date.UTC(p.year, p.month - 1, p.day + dayOffset, h, 0, 0)
  const firstPass = naive - offsetMsAt(z, new Date(naive))
  return new Date(naive - offsetMsAt(z, new Date(firstPass)))
}

/**
 * True when the viewer's own clock IS UTC at `at` — so the viewer-time and true-UTC forms would
 * render the SAME instant twice.
 *
 * Not a corner case: it covers everyone at UTC+0, which includes the UK and Ireland for four
 * months of the year, plus Portugal, Iceland and much of West Africa year-round. Printing
 * "12:00 AM UTC   00:00 UTC" side by side reads as a rendering bug rather than as two frames of
 * reference, so the tag drops the redundant half. The remaining figure still names its zone
 * (`timeZoneName: 'short'` → "UTC"), so nothing is lost.
 */
export function viewerIsUTC(at: Date): boolean {
  return at.getTimezoneOffset() === 0
}

/** The reset instant on the VIEWER's own clock — "5:00 PM PDT". Auto-detected, never picked. */
export function resetInViewerTime(at: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(at)
}

/** The same instant in true UTC — "07:00 UTC". The number everyone can quote to each other. */
export function resetInTrueUTC(at: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())} UTC`
}

/**
 * The reset instant for one offer, resolved from whichever source is available.
 *
 * `serverNextReset` (the server's own `next_reset_utc` / `resets_at_utc`) wins when present — it
 * is computed where the cap day is actually decided. The zone+hour form is the fallback for an
 * offer the payload has not described yet, and for the other tabs in the switcher.
 */
export function offerResetAt(
  offer: { reset_timezone?: string; reset_hour?: number; next_reset_utc?: string } | null | undefined,
  serverNextReset?: string | null,
  now: Date = new Date(),
): Date {
  for (const iso of [serverNextReset, offer?.next_reset_utc]) {
    if (!iso) continue
    const parsed = new Date(iso)
    if (!Number.isNaN(parsed.getTime())) return parsed
  }
  return nextResetAt(offer?.reset_timezone, offer?.reset_hour, now)
}

/** True when this offer is off the default 00:00 UTC day, i.e. the boundary is worth calling out. */
export function hasCustomResetTime(
  tz: string | null | undefined,
  hour: number | null | undefined,
): boolean {
  return safeZone(tz) !== DEFAULT_ZONE || resolveResetHour(hour) !== 0
}

/** "00:00 UTC" / "12:00 AM PDT" — the reset stated on the OFFER's own clock. */
export function resetLabel(
  tz: string | null | undefined,
  hour: number | null | undefined,
  now: Date = new Date(),
): string {
  const z = safeZone(tz)
  const h = resolveResetHour(hour)
  if (z === DEFAULT_ZONE) return `${String(h).padStart(2, '0')}:00 UTC`
  try {
    // Formatted AT the reset instant, so the abbreviation reflects the DST state in force there.
    return new Intl.DateTimeFormat('en-US', {
      timeZone: z, hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(nextResetAt(z, h, now))
  } catch {
    return `${String(h).padStart(2, '0')}:00 UTC`
  }
}
